import { any, apiDate, number, optional, optionalString, string, z, zenum, zodLess, ZodLessInfer, zrecord } from '@kaiizen/zod-less';
import {
  APPLY_SCAN_VIDEO_AUDIO_TO_SCOPE_JOB_PHASES,
  AUDIO_TRANSCRIBE_JOB_PHASES,
  VIDEO_GROUP_IMAGE_CAPTURE_JOB_PHASES,
  VIDEO_TO_SCOPE_JOB_PHASES,
} from '../ScanVideoUpload';
import { LimitSchema } from '../schemaLib';

// ****************************************************************************************
// Async Operation API — see kai-wiki/architecture/rfcs/ASYNC_OPERATION_API_RFC.md
//
// One record shape (`ApiOperationSchema`) is returned by every endpoint: create returns it
// nearly empty, read returns the same record progressively filled in, list returns an array
// of it. `operationType` is a **top-level** discriminant so one check narrows all three
// type-specific fields (`input`, `progressDetails`, `output`) end to end — through the client,
// not just inside the package (the flaw the prior BackgroundJob design had). The remaining
// fields are universal and mean the same thing for every operation.
//
// NAMING (deviation flagged for review): the RFC leaves the exact `operationType` wire values
// open (§9 Q2 muses about dropping the `Job` suffix). To keep a single source of truth and a
// trivial column backfill, `operationType` here **equals the server `JobType`** (the registry
// key, e.g. `ExampleJob`). Dropping the suffix for a customer-facing operation is a per-operation
// rename to make when that operation is converted.
// ****************************************************************************************

/** Universal lifecycle status. Maps 1:1 to the DB `BackgroundJob.lifecycleStatus` enum. */
export const ApiOperationStatusSchema = zodLess('created|started|completed|failed');
export type ApiOperationStatus = ZodLessInfer<typeof ApiOperationStatusSchema>;

/** Array of status values — validates the CSV `status` list on the generic list endpoint. */
export const ApiOperationStatusesSchema = zodLess([ApiOperationStatusSchema]);

/** First-class error, present when `status` is `failed`. */
export const ApiOperationErrorSchema = zodLess({ message: string });
export type ApiOperationError = ZodLessInfer<typeof ApiOperationErrorSchema>;

/**
 * The universal fields, identical for every operation type. `progress` is a universal `0..1`
 * number (a generic progress bar / list row can render any operation from it without knowing
 * the type); the type-specific `input`/`progressDetails`/`output` are added per member below.
 * Optional-and-nullable fields reach the client as **absent** — the response layer strips
 * `null` — so a client treats missing and null identically. Completion is read from `status`,
 * never from the presence of `output` or from `progress` reaching `1`.
 */
const ApiOperationBaseSchema = zodLess({
  id: string,
  status: ApiOperationStatusSchema,
  progress: optional(number), // universal, 0..1
  error: optional(ApiOperationErrorSchema),
  userId: optionalString,
  createdAt: apiDate,
  updatedAt: apiDate,
});

// ****************************************************************************************
// progressDetails — the type-specific "where is it" detail, built from each operation's
// shared phase catalog so the worker's reporter and the client can't drift.
// ****************************************************************************************

/** `zenum` of a catalog's `phase` literals, preserving the exact phase union (not bare `string`). */
const phaseEnumFromCatalog = <C extends readonly { phase: string }[]>(catalog: C) =>
  zenum(...(catalog.map(entry => entry.phase) as [C[number]['phase'], ...C[number]['phase'][]]));

/** progressDetails shape for an instrumented operation: its exact phase enum + the shared detail fields. */
const progressDetailsFromCatalog = <C extends readonly { phase: string }[]>(catalog: C) =>
  zodLess({
    phase: phaseEnumFromCatalog(catalog),
    message: optional(string),
    stepIndex: optional(number),
    totalSteps: optional(number),
    updatedAt: string,
  });

/**
 * progressDetails for operations that report a phase but do not (yet) have a typed catalog —
 * `phase` is a bare string. As each such operation is converted, swap this for a catalog above.
 */
const GenericProgressDetailsSchema = zodLess({
  phase: string,
  message: optional(string),
  stepIndex: optional(number),
  totalSteps: optional(number),
  updatedAt: string,
});

/** progressDetails for the instrumented operations. */
export const OPERATION_PROGRESS_CATALOGS = {
  VideoToScopeJob: VIDEO_TO_SCOPE_JOB_PHASES,
  AudioTranscribeJob: AUDIO_TRANSCRIBE_JOB_PHASES,
  ApplyScanVideoAudioToScopeJob: APPLY_SCAN_VIDEO_AUDIO_TO_SCOPE_JOB_PHASES,
  VideoGroupImageCaptureJob: VIDEO_GROUP_IMAGE_CAPTURE_JOB_PHASES,

  // Deprecated jobs kept so any historically-stored progress still reads back typed.
  ScanVideoAudioExtractJob: VIDEO_TO_SCOPE_JOB_PHASES,
  ScanVideoSpeechlessJob: VIDEO_TO_SCOPE_JOB_PHASES,
} as const;

/** Operation types whose stored progress detail can be validated against a typed catalog. */
export const INSTRUMENTED_OPERATION_TYPES = Object.keys(OPERATION_PROGRESS_CATALOGS) as (keyof typeof OPERATION_PROGRESS_CATALOGS)[];

// ****************************************************************************************
// Per-operation payload schemas
//
// `input` and `output` schemas for each operation belong in this shared package (the union
// cannot be built from schemas only the server can import). Converted operations declare their
// real shapes; operations not yet converted use permissive placeholders (`zrecord(any)` /
// `any`) — read-only until they are wrapped one at a time, at which point their placeholder is
// replaced with the real schema (the breaking change is closing `input`, done per operation).
// ****************************************************************************************

/** ExampleJob — the first fully-typed operation (mirrors `source/Worker/Jobs/ExampleJob.ts`). */
export const ExampleOperationInputSchema = zodLess({ message: string, delay: optional(number) });
export const ExampleOperationOutputSchema = zodLess({ processedMessage: string, timestamp: string });

/**
 * AudioTranscribeJob — **removed** (worker + client-startable path deleted). Union member retained
 * so historical `BackgroundJob` rows still read back typed.
 */
export const AudioTranscribeOperationInputSchema = zodLess({ scanVideoAudioId: string });
export const AudioTranscribeOperationOutputSchema = zodLess({
  scanVideoAudioId: string,
  status: string,
  text: optionalString,
  error: optionalString,
});

/**
 * ApplyScanVideoAudioToScopeJob — **removed** (worker + client-startable path deleted). Union
 * member retained so historical `BackgroundJob` rows still read back typed.
 */
export const ApplyScanVideoAudioToScopeOperationInputSchema = zodLess({
  scanVideoAudioId: string,
  groupId: string,
  groupName: optionalString,
});
export const ApplyScanVideoAudioToScopeOperationOutputSchema = zodLess({
  scanVideoAudioId: string,
  groupId: string,
  status: string,
  appliedTaskCount: optional(number),
  error: optionalString,
});

/**
 * Scan video analysis input — client kickoff sends `videoId`; the server resolves org/property/
 * project/scan/mime before enqueue. Those enriched fields may also appear on read-back for rows
 * created via upload-complete or other server-side paths.
 */
const scanVideoAnalysisOperationInputFields = {
  videoId: string,
  orgId: optionalString,
  propertyId: optionalString,
  projectId: optionalString,
  scanId: optionalString,
  mime: optionalString,
};

/**
 * VideoToScopeJob — the client may also send `scanGroupId`, the Scan Group (room) the video was
 * recorded for, so the job skips identifying rooms from the video.
 */
export const VideoToScopeOperationInputSchema = zodLess({
  ...scanVideoAnalysisOperationInputFields,
  scanGroupId: optionalString,
});

/** VideoGroupImageCaptureJob — client sends `videoId` only. */
export const VideoGroupImageCaptureOperationInputSchema = zodLess(scanVideoAnalysisOperationInputFields);

const PlaceholderInputSchema = zrecord(any);

// ****************************************************************************************
// The union — one member per operation type, declared explicitly.
//
// Members are written out (not mapped over a registry): a `.map(...)` widens the
// `operationType` literal back to `string` and destroys the narrowing the union exists for.
// Every member carries all three type-specific fields so `ApiOperationOf<T>[...]` resolves for
// each key even when the value is a placeholder.
// ****************************************************************************************
export const ApiOperationSchema = z.discriminatedUnion('operationType', [
  // ---- Converted ----------------------------------------------------------------------
  ApiOperationBaseSchema.extend({
    operationType: z.literal('ExampleJob'),
    input: ExampleOperationInputSchema,
    progressDetails: optional(GenericProgressDetailsSchema),
    output: optional(ExampleOperationOutputSchema),
  }),
  ApiOperationBaseSchema.extend({
    operationType: z.literal('AudioTranscribeJob'),
    input: AudioTranscribeOperationInputSchema,
    progressDetails: optional(progressDetailsFromCatalog(AUDIO_TRANSCRIBE_JOB_PHASES)),
    output: optional(AudioTranscribeOperationOutputSchema),
  }),
  ApiOperationBaseSchema.extend({
    operationType: z.literal('ApplyScanVideoAudioToScopeJob'),
    input: ApplyScanVideoAudioToScopeOperationInputSchema,
    progressDetails: optional(progressDetailsFromCatalog(APPLY_SCAN_VIDEO_AUDIO_TO_SCOPE_JOB_PHASES)),
    output: optional(ApplyScanVideoAudioToScopeOperationOutputSchema),
  }),
  ApiOperationBaseSchema.extend({
    operationType: z.literal('VideoToScopeJob'),
    input: VideoToScopeOperationInputSchema,
    progressDetails: optional(progressDetailsFromCatalog(VIDEO_TO_SCOPE_JOB_PHASES)),
    output: optional(any),
  }),
  ApiOperationBaseSchema.extend({
    operationType: z.literal('VideoGroupImageCaptureJob'),
    input: VideoGroupImageCaptureOperationInputSchema,
    progressDetails: optional(progressDetailsFromCatalog(VIDEO_GROUP_IMAGE_CAPTURE_JOB_PHASES)),
    output: optional(any),
  }),

  // ---- Instrumented (typed progressDetails; input/output pending conversion) ----------

  // ---- Pending conversion (permissive placeholders) -----------------------------------
  ApiOperationBaseSchema.extend({
    operationType: z.literal('PDFExportJob'),
    input: PlaceholderInputSchema,
    progressDetails: optional(GenericProgressDetailsSchema),
    output: optional(any),
  }),
  ApiOperationBaseSchema.extend({
    operationType: z.literal('ExcelPOExportJob'),
    input: PlaceholderInputSchema,
    progressDetails: optional(GenericProgressDetailsSchema),
    output: optional(any),
  }),
  ApiOperationBaseSchema.extend({
    operationType: z.literal('BillingExcelExportJob'),
    input: PlaceholderInputSchema,
    progressDetails: optional(GenericProgressDetailsSchema),
    output: optional(any),
  }),
  ApiOperationBaseSchema.extend({
    operationType: z.literal('BulkExportArtifacts'),
    input: PlaceholderInputSchema,
    progressDetails: optional(GenericProgressDetailsSchema),
    output: optional(any),
  }),
  ApiOperationBaseSchema.extend({
    operationType: z.literal('CalcTotalCostsByProductSupplier'),
    input: PlaceholderInputSchema,
    progressDetails: optional(GenericProgressDetailsSchema),
    output: optional(any),
  }),
  ApiOperationBaseSchema.extend({
    operationType: z.literal('ScanPhotoScaleJob'),
    input: PlaceholderInputSchema,
    progressDetails: optional(GenericProgressDetailsSchema),
    output: optional(any),
  }),
]);
export type ApiOperation = ZodLessInfer<typeof ApiOperationSchema>;

/** Union of every operation type's wire value (the discriminant). */
export type ApiOperationType = ApiOperation['operationType'];

/** The single operation record narrowed to one operation type — used by the per-operation client hooks. */
export type ApiOperationOf<T extends ApiOperationType> = Extract<ApiOperation, { operationType: T }>;

// ****************************************************************************************
// Request schemas
// ****************************************************************************************

/**
 * Create body — the record's own two client-owned fields. Params nest under `input` (rather than
 * spreading flat alongside `operationType`) so operation params never share a namespace with
 * envelope fields; the Model boundary validates `input` against the operation's registry schema
 * and derives org/user from the session. `input` is a permissive record on the wire and is
 * narrowed per operation server-side.
 */
export const ApiCreateOperationSchema = zodLess({
  operationType: string,
  input: zrecord(any),
});
export type ApiCreateOperation = ZodLessInfer<typeof ApiCreateOperationSchema>;

const csvQuerySchema = z.preprocess(
  value =>
    Array.isArray(value)
      ? value.map(item => `${item}`.trim()).filter(Boolean)
      : typeof value === 'string'
      ? value.split(',').map(item => item.trim()).filter(Boolean)
      : value,
  z.array(string),
);

/**
 * List query. `status` is **CSV** ("still running" = `created,started` — the main reason to filter
 * by status, and not expressible with a single value). `operationType` filters to **one** operation
 * type (the per-operation client hooks depend on it). `limit` defaults to and is capped at 100
 * server-side. Results are ordered `updatedAt` desc. Org scope comes from the session, never a param.
 */
export const OperationListQuerySchema = zodLess({
  status: optional(csvQuerySchema).meta({
    description: 'Filter by one or more status values (CSV): created,started,completed,failed. "Still running" = created,started.',
  }),
  operationType: optionalString.meta({ description: 'Filter to a single operation type, e.g. VideoToScopeJob.' }),
  limit: optional(LimitSchema).meta({ description: 'Max rows (default 100, capped at 100), ordered updatedAt desc.' }),
});
export type OperationListQuery = ZodLessInfer<typeof OperationListQuerySchema>;
